// Turns the call, journey, transcript and analysis responses into what the call detail page shows
// (mockup docs/design/mockups/_build/pages/13-call-detail.html). Pure functions; times are seconds.
import { fmt } from '../../../design/format';
import { routeKind } from '../callFormat';
import { displaySentiment, languageName } from '../callFilters';

export const SPEAKER_COLORS = ['var(--series-1)', 'var(--series-3)', 'var(--series-7)', 'var(--series-5)', 'var(--series-4)'];
export const SYSTEM_COLOR = 'var(--border-strong)';

const TYPE_LABEL = {
  AutoAttendant: 'Auto attendant',
  Queue: 'Queue',
  Agent: 'Agent',
  Transfer: 'Transfer to queue',
  Voicemail: 'Voicemail',
  Forward: 'Forward',
  Abandoned: 'Abandoned',
  External: 'External number',
};

export const LEGEND_KINDS = [
  ['aa', 'Auto attendant'],
  ['queue', 'Queue'],
  ['agent', 'Agent'],
  ['transfer', 'Transfer'],
  ['forward', 'Forward'],
  ['voicemail', 'Voicemail'],
  ['abandon', 'Abandoned'],
  ['external', 'External number'],
];

const GENERIC_LABEL = /^(speaker|guest|spk|agent|caller)[\s_-]*\d+$/i;

const sortedSegments = (transcript) =>
  [...(transcript?.segments || [])].map((s, i) => ({ ...s, index: i })).sort((a, b) => a.startMs - b.startMs || a.endMs - b.endMs);

/** Mulberry-style generator from the mockups (VL.rng), for the decorative waveform bars. */
export function rng(seed) {
  let s = (Math.imul(seed + 0x9e3779b9, 2654435761) ^ 0x85ebca6b) >>> 0;
  const next = () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  next();
  return next;
}

export function seedOf(text) {
  let h = 0;
  const s = String(text || '');
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h % 100000;
}

/** Route hops with durations and captions (lineage card). */
const ENDED_BY = { Caller: '; caller hung up', Agent: '; agent ended call', System: '; call dropped' };

export function buildRoute(journey) {
  const steps = [...(journey?.steps || [])].sort((a, b) => a.order - b.order);
  return steps.map((s, i) => {
    const next = steps[i + 1];
    let caption = '';
    if (s.type === 'AutoAttendant') caption = s.menuSelection ? `Caller chose ${s.menuSelection}` : s.menuOption || '';
    else if (s.type === 'Queue') caption = next || s.outcome !== 'Abandoned' ? 'Waited in queue' : 'Caller hung up while waiting';
    else if (s.type === 'Agent') caption = `${s.holdMs ? `Talked, including ${fmt.mmss(s.holdMs / 1000)} on hold` : 'Talked'}${ENDED_BY[s.endedBy] || ''}`;
    else if (s.type === 'Transfer') {
      const how = s.transferType === 'Blind' ? 'Blind transfer' : s.transferType === 'Consultative' ? 'Consultative transfer' : 'Transfer';
      caption = s.sourceEntityName ? `${how} by ${s.sourceEntityName}` : how;
    } else if (s.type === 'Voicemail') caption = 'Left a voicemail';
    else if (s.type === 'Abandoned') caption = 'Caller hung up';
    else if (s.type === 'Forward') caption = s.phoneNumber ? `Forwarded to ${s.phoneNumber}` : 'Forwarded';
    else if (s.type === 'External') caption = s.phoneNumber || '';
    return { kind: routeKind(s.type), type: s.type, label: TYPE_LABEL[s.type] || s.type, name: s.entityName || TYPE_LABEL[s.type] || s.type, seconds: Math.max(0, s.duration || 0), caption, step: s };
  });
}

/** Seconds from the call start to the first recorded audio (the first agent answering). */
export function recordingOffsetSeconds(analysis, route) {
  if (analysis?.recordingStartOffsetMs != null) return analysis.recordingStartOffsetMs / 1000;
  let t = 0;
  for (const hop of route) {
    if (hop.type === 'Agent') return t;
    t += hop.seconds;
  }
  return 0;
}

/** Speakers in order of first appearance: the caller first, then agents named from the journey when the labels are generic. */
export function buildSpeakers(transcript, analysis, call, route) {
  const segments = sortedSegments(transcript);
  const agentNames = [...new Set(route.filter((h) => h.type === 'Agent').map((h) => h.name))];
  const speakers = [];
  const byLabel = {};
  const callerName = analysis?.caller?.name || call?.callerName || 'Caller';
  segments.forEach((s) => {
    const label = s.speakerLabel || s.speakerRole || 'Speaker';
    if (byLabel[label]) return;
    const isCaller = s.speakerRole === 'Caller';
    let speaker;
    if (isCaller) speaker = { key: 'caller', name: callerName, isCaller: true };
    else {
      const agentIndex = speakers.filter((x) => !x.isCaller).length;
      const name = GENERIC_LABEL.test(label) || !s.speakerLabel ? agentNames[agentIndex] || `Speaker ${agentIndex + 1}` : label;
      speaker = { key: `a${agentIndex + 1}`, name, isCaller: false, automated: s.speakerRole === 'Automated' };
    }
    if (isCaller && speakers.some((x) => x.isCaller)) {
      byLabel[label] = speakers.find((x) => x.isCaller);
      return;
    }
    speakers.push(speaker);
    byLabel[label] = speaker;
  });
  const ordered = [...speakers.filter((s) => s.isCaller), ...speakers.filter((s) => !s.isCaller)];
  let agentSlot = 1;
  ordered.forEach((s) => {
    s.color = s.isCaller ? SPEAKER_COLORS[0] : SPEAKER_COLORS[Math.min(SPEAKER_COLORS.length - 1, agentSlot++)];
    if (s.automated) s.color = SYSTEM_COLOR;
  });
  return { speakers: ordered, byLabel };
}

const normalize = (text) => String(text || '').toLowerCase();

/**
 * Evidence marks: each stored quote is located in the transcript segment it came from (time window ± 2 s, as the
 * verifier checks it). Quotes with the same text in the same segment share a mark. Returns { marks, quoteMark }:
 * marks by segment index ([{ id, start, end }], non-overlapping; overlapping quotes share one mark covering both) and
 * the mark id of every quote (or null).
 */
export function locateEvidence(transcript, quotes) {
  const segments = sortedSegments(transcript);
  const marks = {};
  const quoteMark = new Map();
  quotes.forEach((q) => {
    const needle = normalize(q.quote).trim();
    if (!needle) {
      quoteMark.set(q, null);
      return;
    }
    const candidates = segments.filter((s) => q.startMs >= s.startMs - 2000 && q.startMs <= s.endMs + 2000 && normalize(s.text).includes(needle));
    const segment = candidates[0] || segments.find((s) => normalize(s.text).includes(needle));
    if (!segment) {
      quoteMark.set(q, null);
      return;
    }
    const start = normalize(segment.text).indexOf(needle);
    const end = start + needle.length;
    const list = marks[segment.index] || (marks[segment.index] = []);
    const same = list.find((m) => m.start === start && m.end === end);
    if (same) {
      quoteMark.set(q, same.id);
      return;
    }
    const overlapping = list.find((m) => start < m.end && end > m.start);
    if (overlapping) {
      overlapping.start = Math.min(overlapping.start, start);
      overlapping.end = Math.max(overlapping.end, end);
      quoteMark.set(q, overlapping.id);
      return;
    }
    const mark = { id: `ev-${segment.index}-${start}`, start, end, startMs: segment.startMs };
    list.push(mark);
    quoteMark.set(q, mark.id);
  });
  Object.values(marks).forEach((list) => list.sort((a, b) => a.start - b.start));
  return { marks, quoteMark };
}

const REDACTION = /\[[^\]]*redacted\]/gi;

const REDACTION_LABELS = { 'payment card number redacted': 'card number redacted' };

/** The pill text for a stored redaction token ("[payment card number redacted]" reads "card number redacted"). */
export const redactionLabel = (token) => REDACTION_LABELS[token.toLowerCase()] || token;

/** Splits a segment's text into plain, evidence-mark and redaction runs. */
export function textRuns(text, marks = []) {
  const ranges = [];
  marks.forEach((m) => ranges.push({ start: m.start, end: m.end, kind: 'mark', id: m.id }));
  let match;
  REDACTION.lastIndex = 0;
  while ((match = REDACTION.exec(text))) {
    const start = match.index;
    const end = start + match[0].length;
    if (!ranges.some((r) => start < r.end && end > r.start)) ranges.push({ start, end, kind: 'redaction' });
  }
  ranges.sort((a, b) => a.start - b.start);
  const runs = [];
  let at = 0;
  ranges.forEach((r) => {
    if (r.start < at) return;
    if (r.start > at) runs.push({ kind: 'text', text: text.slice(at, r.start) });
    const body = text.slice(r.start, r.end);
    runs.push(r.kind === 'redaction' ? { kind: 'redaction', text: redactionLabel(body.slice(1, -1)) } : { kind: 'mark', id: r.id, text: body });
    at = r.end;
  });
  if (at < text.length) runs.push({ kind: 'text', text: text.slice(at) });
  return runs;
}

/**
 * Transcript turns plus the system lines the mockup shows ("On hold 4:02", "Transferred to … · waited 0:34"),
 * placed from the journey on the recording's clock.
 */
export function buildTurns(transcript, analysis, route, byLabel, recordingSeconds) {
  const segments = sortedSegments(transcript);
  const offset = recordingOffsetSeconds(analysis, route);
  const translations = analysis?.translatedSegments || {};
  const turns = segments.map((s) => {
    const speaker = byLabel[s.speakerLabel || s.speakerRole || 'Speaker'];
    return {
      kind: 'segment',
      t: s.startMs / 1000,
      end: s.endMs / 1000,
      index: s.index,
      speaker,
      text: s.text,
      translation: translations[s.index] ?? translations[String(s.index)] ?? null,
    };
  });

  const system = [];
  let callClock = 0;
  route.forEach((hop) => {
    const startOnRecording = callClock - offset;
    const endOnRecording = startOnRecording + hop.seconds;
    if (hop.type === 'Agent' && hop.step.holdMs > 0) {
      const inside = turns.filter((t) => t.t >= startOnRecording - 1 && t.t <= endOnRecording + 1);
      let best = null;
      for (let i = 0; i < inside.length - 1; i += 1) {
        const gap = inside[i + 1].t - inside[i].end;
        if (!best || gap > best.gap) best = { gap, at: inside[i].end };
      }
      if (best && best.gap > 5) system.push({ kind: 'system', t: best.at, text: `On hold ${fmt.mmss(hop.step.holdMs / 1000)}` });
    }
    if (hop.type === 'Transfer' && startOnRecording >= 0 && startOnRecording <= recordingSeconds) {
      system.push({ kind: 'system', t: startOnRecording, text: `Transferred to ${hop.name} · waited ${fmt.mmss(hop.seconds)}` });
    }
    callClock += hop.seconds;
  });

  return [...turns, ...system].sort((a, b) => a.t - b.t || (a.kind === 'system' ? -1 : 1));
}

/** Waveform bars (mockup drawWave): 150 bars over the recording, coloured by who is speaking. */
export function waveBars(turns, recordingSeconds, seed, count = 150, width = 600, height = 56) {
  const r = rng(seed);
  const spans = turns.map((t, i) => [t.t, i + 1 < turns.length ? turns[i + 1].t : recordingSeconds, t]);
  const bars = [];
  for (let k = 0; k < count; k += 1) {
    const t = (k / count) * recordingSeconds;
    const span = spans.find((x) => t >= x[0] && t < x[1]);
    const turn = span ? span[2] : null;
    const system = !turn || turn.kind === 'system';
    const amp = system ? 0.06 : 0.25 + r() * 0.7;
    const h = Math.max(2, amp * (height - 6));
    bars.push({ x: (k * width) / count + 0.6, y: (height - h) / 2, w: width / count - 1.4, h, color: system ? SYSTEM_COLOR : turn.speaker?.color || SYSTEM_COLOR });
  }
  return bars;
}

const SENTIMENT_VALUE = { VeryNegative: -1, Negative: -0.5, Mixed: 0, Neutral: 0, Positive: 0.5, VeryPositive: 1 };
export const sentimentValue = (level) => (level in SENTIMENT_VALUE ? SENTIMENT_VALUE[level] : null);

/** Caller line from the sentiment timeline; the agent line is the analysis' single agent sentiment. */
export function sentimentSeries(analysis) {
  const points = (analysis?.sentimentTimeline || []).filter((p) => sentimentValue(p.level) != null);
  if (!points.length) return null;
  const agent = sentimentValue(analysis?.sentiment?.agent);
  return {
    labels: points.map((p) => fmt.mmss(p.offsetMs / 1000)),
    caller: points.map((p) => sentimentValue(p.level)),
    agent: agent == null ? null : points.map(() => agent),
  };
}

const ROLE_LABEL = { Owner: 'Owner', Tenant: 'Tenant', BoardMember: 'Board member', Realtor: 'Realtor', TitleCompany: 'Title company', Vendor: 'Vendor' };

/** "Board member" (sentence case, as the reports mockup) for the analysis caller role; empty when Unknown or absent. */
export const callerRoleLabel = (role) => ROLE_LABEL[role] || '';

/** Caller tile caption: role and lot ("Owner, Lot 214"), whichever the analysis verified. */
export const callerCaption = (analysis) => [callerRoleLabel(analysis?.caller?.role), analysis?.property?.unitOrLot].filter(Boolean).join(', ');

/** Sentiment tile caption: "improved after work order" from the turning point; none without one (the Spanish mockup call). */
export function sentimentCaption(analysis) {
  const verb = { Improving: 'improved', Declining: 'worsened' }[analysis?.sentiment?.trajectory];
  const turn = (analysis?.sentimentTurningPoint || '').trim();
  return verb && turn ? `${verb} ${turn}` : '';
}

export function sentimentNote(sentiment) {
  const start = displaySentiment(sentiment?.callerStart);
  const end = displaySentiment(sentiment?.callerEnd);
  if (!start && !end) return '';
  const trend = { Improving: 'improved during the call', Declining: 'worsened during the call', Stable: 'stayed about the same' }[sentiment?.trajectory];
  if (start && end && start !== end) return `Started ${start.toLowerCase()}; ended ${end.toLowerCase()}${trend ? ` (${trend})` : ''}.`;
  return `Caller sentiment ${(end || start).toLowerCase()}${trend ? `; ${trend}` : ''}.`;
}

const PROVIDER_LABEL = { Claude: 'Claude via Anthropic API', AzureOpenAi: 'Azure OpenAI', Local: 'Local language model', Synthetic: 'Synthetic (demo data)' };
const TRANSCRIPTION_LABEL = { AzureAiSpeech: 'Azure AI Speech', LocalWhisper: 'Local Whisper', TeamsNative: 'Teams transcript', Synthetic: 'Synthetic (demo data)' };
const METHOD_LABEL = {
  SpeechEngineLanguageIdentification: 'Speech engine identification',
  LanguageModelConfirmation: 'Language model confirmation',
  Configured: 'Configured language',
  Unknown: 'Not recorded',
};
const REDACTION_LABEL = {
  PaymentCardNumber: ['card number', 'card numbers'],
  BankAccountNumber: ['bank account number', 'bank account numbers'],
  BankRoutingNumber: ['routing number', 'routing numbers'],
  SocialSecurityNumber: ['Social Security number', 'Social Security numbers'],
};

export const providerLabel = (provider) => PROVIDER_LABEL[provider] || provider || 'Language model';
export const transcriptionLabel = (provider) => TRANSCRIPTION_LABEL[provider] || provider || 'Transcription';
export const methodLabel = (method) => METHOD_LABEL[method] || method || 'Not recorded';

/** "1 card number" / "2 card numbers" for the transcript's redactions (null when nothing was redacted). */
export function redactionPhrase(transcript) {
  const count = transcript?.redactionCount || 0;
  if (!count) return null;
  const kinds = transcript.redactionKinds || [];
  if (kinds.length === 1 && REDACTION_LABEL[kinds[0]]) return `${count} ${REDACTION_LABEL[kinds[0]][count === 1 ? 0 : 1]}`;
  return `${count} ${count === 1 ? 'number' : 'numbers'}`;
}

export const digits = (value) => String(value || '').replace(/\D/g, '').slice(-10);

/**
 * Callback number row: the number the analysis verified, flagged when it matches the Teams caller ID; without one,
 * the call's caller ID, labelled as such.
 */
export function callbackNumber(analysis, call) {
  const verified = analysis?.caller?.callbackNumber;
  if (verified) return { number: verified, fromCallerId: false, matchesCallerId: Boolean(digits(verified)) && digits(verified) === digits(call?.callerNumber) };
  if (call?.callerNumber) return { number: call.callerNumber, fromCallerId: true, matchesCallerId: false };
  return null;
}

export { displaySentiment, languageName };
